'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { SUPPORTED_CURRENCIES, CurrencyCode } from '@/lib/travelpayouts';

interface CurrenciesTabProps {
  selectedCurrency: CurrencyCode;
  onSelect: (currency: CurrencyCode) => void;
}

const POPULAR_CURRENCIES: CurrencyCode[] = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'INR', 'SGD'];

export default function CurrenciesTab({ selectedCurrency, onSelect }: CurrenciesTabProps) {
  const popular = SUPPORTED_CURRENCIES.filter((c) => POPULAR_CURRENCIES.includes(c.code as CurrencyCode));
  const others = SUPPORTED_CURRENCIES.filter((c) => !POPULAR_CURRENCIES.includes(c.code as CurrencyCode));

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
          <Icon name="CurrencyDollarIcon" size={18} className="text-primary" />
        </div>
        <div>
          <h3 className="text-sm font-700 text-foreground">Display Currency</h3>
          <p className="text-xs text-muted-foreground">All prices shown in your selected currency</p>
        </div>
      </div>

      {/* Popular Currencies */}
      <div>
        <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Popular</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {popular.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => onSelect(c.code as CurrencyCode)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-sm ${
                selectedCurrency === c.code
                  ? 'border-primary bg-secondary text-primary font-700' :'border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted'
              }`}
            >
              <span className="text-base font-700 w-5 text-center">{c.symbol}</span>
              <div className="text-left">
                <p className="font-semibold text-xs leading-tight">{c.code}</p>
                <p className="text-[10px] text-muted-foreground leading-tight truncate">{c.name.split(' ')[0]}</p>
              </div>
              {selectedCurrency === c.code && (
                <Icon name="CheckCircleIcon" size={14} className="text-primary ml-auto flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Other Currencies */}
      <div>
        <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">All Currencies</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {others.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => onSelect(c.code as CurrencyCode)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-sm ${
                selectedCurrency === c.code
                  ? 'border-primary bg-secondary text-primary font-700' :'border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted'
              }`}
            >
              <span className="text-base font-700 w-5 text-center">{c.symbol}</span>
              <div className="text-left">
                <p className="font-semibold text-xs leading-tight">{c.code}</p>
                <p className="text-[10px] text-muted-foreground leading-tight truncate">{c.name.split(' ')[0]}</p>
              </div>
              {selectedCurrency === c.code && (
                <Icon name="CheckCircleIcon" size={14} className="text-primary ml-auto flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Info note */}
      <div className="flex items-start gap-2 bg-muted/60 rounded-xl p-3">
        <Icon name="InformationCircleIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Prices are powered by <span className="font-semibold text-foreground">Travelpayouts</span> and converted to your selected currency. Final prices may vary at checkout.
        </p>
      </div>
    </div>
  );
}
